import React, { useMemo, useCallback, useState } from 'react';
import type { SingleValue } from 'react-select';

import type { SingleValueProps, OptionProps, OptionType } from '@atlaskit/select';
import Select, { components } from '@atlaskit/select';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss, Box, Text, Inline } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { layers } from '@atlaskit/theme/constants';

import type { SpaceRole } from '../model/space-roles-types.ts';

interface Option {
	id: string;
	label: string;
	value: string;
	description: string;
	ariaLabel: string;
}

interface RoleSelectorProps {
	initialRoleId?: string | null;
	value?: string | null | OptionType;
	isLoading?: boolean;
	placeholder?: React.ReactNode;
	onChange?: (role: SpaceRole) => void;
	spaceRoleOptions: Pick<SpaceRole, 'id' | 'name' | 'description' | 'roleType'>[];
	onMenuOpen?: () => void;
	onMenuClose?: () => void;
	menuPlacement?: 'auto' | 'bottom' | 'top';
	appearance?: 'none' | 'default' | 'subtle';
	spacing?: 'default' | 'compact';
	noOptionsMessage?: string;
	menuPortalTarget?: HTMLElement;
	/**
	 * Maximum height of the dropdown menu in pixels.
	 */
	maxMenuHeight?: number;
	/**
	 * Function to render additional content (like a lozenge) next to a role option.
	 * Returns a React node for the given role ID, or null/undefined to render nothing.
	 */
	renderOptionSuffix?: (roleId: string) => React.ReactNode;
	/**
	 * Optional footer content to render at the bottom of the menu dropdown.
	 * The footer is sticky and stays visible while scrolling through options.
	 * Can be a React node or a render function that receives a closeMenu callback.
	 */
	menuFooter?: React.ReactNode | ((closeMenu: () => void) => React.ReactNode);
	/**
	 * Id for the underlying combobox input. Use with a matching <label htmlFor="..."> for accessibility.
	 */
	inputId?: string;
}

const menuItemStyle = xcss({
	display: 'flex',
	flexDirection: 'column',
	gap: 'space.050',
});

const menuContainerStyle = xcss({
	display: 'flex',
	flexDirection: 'column',
	maxHeight: '320px',
});

const menuScrollableStyle = xcss({
	flex: 1,
	overflowY: 'auto',
	minHeight: '0',
});

export const RoleSelector: React.FC<RoleSelectorProps> = ({
	initialRoleId,
	value,
	isLoading,
	onChange,
	placeholder,
	spaceRoleOptions,
	onMenuClose,
	onMenuOpen,
	menuPlacement = 'auto',
	appearance = 'subtle',
	noOptionsMessage = '',
	spacing = 'default',
	menuPortalTarget,
	maxMenuHeight = 320,
	renderOptionSuffix,
	menuFooter,
	inputId,
}) => {
	const [internalVal, setInternalVal] = useState<string | OptionType | null>(
		(value === undefined ? initialRoleId : value) || null,
	);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const closeMenu = useCallback(() => {
		setIsMenuOpen(false);
	}, []);

	// The component is controlled if value is not `undefined`. `initialRole` will ignored in controlled mode.
	const isControlledComponent = value !== undefined;

	const options: Option[] = useMemo(
		() =>
			spaceRoleOptions.map((role) => ({
				id: role?.id ?? '',
				label: role?.name ?? '',
				value: role?.id ?? '',
				description: role?.description ?? '',
				ariaLabel: `${role?.name} - ${role?.description ?? ''}`,
			})),
		[spaceRoleOptions],
	);

	const getOptionFromValue = useCallback(
		(val: OptionType | string | null | undefined): Option | null => {
			if (!val) return null;

			return typeof val === 'string'
				? options.find((option) => option.value === val) || null
				: {
						id: val.value.toString(),
						label: val.label,
						value: val.value.toString(),
						description: '',
						ariaLabel: `${val.label} - ${val.description ?? ''}`,
					};
		},
		[options],
	);

	const CustomSingleValue = React.memo((props: SingleValueProps<Option>) => {
		const suffix = renderOptionSuffix?.(props.data.id);
		return (
			<components.SingleValue {...props} aria-label={props.data.ariaLabel}>
				{suffix ? (
					<Inline space="space.100" alignBlock="center">
						<span>{props.data.label}</span>
						{suffix}
					</Inline>
				) : (
					props.data.label
				)}
			</components.SingleValue>
		);
	});

	const CustomOption = React.memo((props: OptionProps<Option>) => {
		const suffix = renderOptionSuffix?.(props.data.id);
		return (
			<components.Option {...props} aria-label={props.data.ariaLabel}>
				<Box xcss={menuItemStyle}>
					{suffix ? (
						<Inline space="space.100" alignBlock="center">
							<Text>{props.data.label}</Text>
							{suffix}
						</Inline>
					) : (
						<Text>{props.data.label}</Text>
					)}
					<Text color="color.text.subtlest">{props.data.description}</Text>
				</Box>
			</components.Option>
		);
	});

	// Resolve menuFooter - call it if it's a function
	const resolvedMenuFooter = useMemo(() => {
		if (!menuFooter) return null;
		return typeof menuFooter === 'function' ? menuFooter(closeMenu) : menuFooter;
	}, [menuFooter, closeMenu]);

	// Custom Menu component with optional footer
	const CustomMenu = useMemo(() => {
		if (!resolvedMenuFooter) return undefined;

		return React.memo((props: any) => (
			<components.Menu {...props}>
				<Box xcss={menuContainerStyle}>
					<Box xcss={menuScrollableStyle}>{props.children}</Box>
					{resolvedMenuFooter}
				</Box>
			</components.Menu>
		));
	}, [resolvedMenuFooter]);

	const handleChange = (newSelection: SingleValue<Option>) => {
		const newSelectionType = spaceRoleOptions.find(
			(role) => role?.id === newSelection?.value,
		)?.roleType;
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				actionSubject: 'button',
				action: 'clicked',
				actionSubjectId: 'change',
				source: 'spaceRoles-roleSelector',
				attributes: {
					newSelection: newSelection?.id,
					newSelectionType,
				},
			},
		}).fire();

		setInternalVal(newSelection?.value || null);
		onChange?.(spaceRoleOptions.find((role) => role?.id === newSelection?.value) as SpaceRole);
	};

	const handleMenuOpen = useCallback(
		(currentRoleSelection: Option | null) => {
			onMenuOpen?.();
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					actionSubject: 'button',
					action: 'clicked',
					actionSubjectId: 'open',
					source: 'spaceRoles-roleSelector',
					attributes: { currentRoleSelection: currentRoleSelection?.id },
				},
			}).fire();
		},
		[createAnalyticsEvent, onMenuOpen],
	);

	const SELECT_STYLE_PROPS = {
		isSearchable: false,
		styles: {
			menu: (base: any) => ({
				...base,
				width: '320px',
				padding: 0,
				margin: 0,
				zIndex: layers.modal() + 1, // Higher than modal to appear above modal content
			}),
			menuPortal: (base: any) => ({
				...base,
				zIndex: layers.modal() + 1, // Higher than modal to appear above modal content
			}),
		},
	};

	const selectValue = isControlledComponent
		? getOptionFromValue(value)
		: getOptionFromValue(internalVal);

	const handleMenuOpenInternal = useCallback(() => {
		setIsMenuOpen(true);
		handleMenuOpen(getOptionFromValue(isControlledComponent ? value : internalVal));
	}, [getOptionFromValue, handleMenuOpen, isControlledComponent, value, internalVal]);

	const handleMenuCloseInternal = useCallback(() => {
		setIsMenuOpen(false);
		onMenuClose?.();
	}, [onMenuClose]);

	return (
		<Select
			data-testid="space-roles-table-role-selector"
			inputId={inputId}
			placeholder={isLoading ? '' : placeholder}
			options={options}
			appearance={appearance}
			isLoading={isLoading}
			onChange={handleChange}
			onMenuOpen={handleMenuOpenInternal}
			onMenuClose={handleMenuCloseInternal}
			menuIsOpen={isMenuOpen}
			components={{
				SingleValue: CustomSingleValue,
				Option: CustomOption,
				...(CustomMenu ? { Menu: CustomMenu } : {}),
			}}
			menuPosition="fixed"
			menuPlacement={menuPlacement}
			menuPortalTarget={menuPortalTarget}
			maxMenuHeight={maxMenuHeight}
			scrollBehavior="outside"
			defaultValue={getOptionFromValue(initialRoleId)}
			value={selectValue}
			noOptionsMessage={() => noOptionsMessage}
			spacing={spacing}
			{...SELECT_STYLE_PROPS}
		/>
	);
};
